/**
 * M-1 — the consent screen's three calls against the CP authorization server.
 *
 * ⛔ **A NEW module, not an addition to an existing one.** This is the only client
 * surface in the console reachable **pre-authentication**, and every other
 * `src/api/*.ts` module is imported only from authenticated panes.
 * `permission-gate-parity.test.ts` reasons over *"a component that imports a
 * permissioned api module owes a gate"* — folding these three calls into an existing
 * module would drag that obligation onto a screen that correctly has **no** gate.
 *
 * The view type is `CpAuthorizeRequestView` of `@zion/sdk-contracts` (plan
 * 2026-10-28-identity-mcp-tools, R-020, default F1: the contract schema over a local copy).
 * The DP view `authorizeRequestViewSchema` requires `orgName`, which the CP does not have, so the
 * contract holds a CP shape of its own, `cpAuthorizeRequestViewSchema`. This module reads the
 * body with no parse: a strict parse would turn a field that a newer cp-auth-api adds into a
 * dead consent page for the length of a rollout.
 *
 * ## ⛔ There is no permission gate on any of the three routes, and that is CORRECT
 *
 * The operator's authority is resolved **from the database, not from the JWT claims**,
 * inside the producer's `getAuthorizeView`: *"the token may be up to one access-token TTL
 * stale, and consent must reflect CURRENT authority."* ⇒ the page renders `scope.held` as
 * the server computed it and ⛔ **must never re-derive it from `useHasPermission`**. There
 * is no manifest entry and no `ForbiddenState` on this screen — it is not a section pane.
 */
import type { CpAuthorizeRequestView } from '@zion/sdk-contracts';
import { api, ApiError } from '../platform/http/client';

/** Both decision routes answer with the top-level URL the browser must go to. */
export interface ConsentDecisionResult {
  readonly redirectTo: string;
}

/**
 * The controller prefix plus the authorize leaf, absolute.
 *
 * These paths are advertised by the RFC 8414 discovery document and clients **cache**
 * it; the producer asserts its own decorator arguments against that document at module
 * load. This is the browser side of the same contract.
 */
const AUTHORIZE_BASE = '/v1/auth/oauth2/authorize';

/**
 * `:id` goes into a path SEGMENT, so it is encoded rather than interpolated raw.
 *
 * The page validates it as a UUID before ever calling here and the server validates it
 * again with `z.string().uuid()`; this is the **third** layer, and the one that keeps a
 * hostile `?request=` out of the request line.
 */
export function authorizePath(id: string, suffix = ''): string {
  return `${AUTHORIZE_BASE}/${encodeURIComponent(id)}${suffix}`;
}

/** Load the pending authorization request. */
export function getAuthorizeRequest(id: string): Promise<CpAuthorizeRequestView> {
  return api.get<CpAuthorizeRequestView>(authorizePath(id));
}

/** Approve. The body is deliberately empty; the server ignores it. */
export function approveAuthorizeRequest(id: string): Promise<ConsentDecisionResult> {
  return api.post<ConsentDecisionResult>(authorizePath(id, '/approve'));
}

/** Deny. Answers with an `error=access_denied` redirect target. */
export function denyAuthorizeRequest(id: string): Promise<ConsentDecisionResult> {
  return api.post<ConsentDecisionResult>(authorizePath(id, '/deny'));
}

/**
 * The RFC 6749 §5.2 `error` code, when the body is one.
 *
 * ⚠️ The producer's OAuth error filter answers `{error, error_description}`, while Nest's
 * own 404 envelope answers `{statusCode, message, error: 'Not Found'}`. **Both have an
 * `error` key** — which is exactly why the caller compares against a specific CODE rather
 * than merely testing for the key's presence.
 */
export function oauthErrorCode(body: unknown): string | null {
  if (typeof body !== 'object' || body === null || !('error' in body)) return null;
  const code = (body as { readonly error: unknown }).error;
  return typeof code === 'string' ? code : null;
}

/** A dead end — no retry affordance, because retrying cannot help. */
export type TerminalKind = 'invalid-or-expired' | 'insufficient-permissions';

/**
 * Map a thrown value onto a page state.
 *
 * ⛔⛔ **`invalid_scope` is matched on the ERROR CODE, not on a bare 4xx, and it is
 * matched FIRST.** Approve answers `403 {error:'invalid_scope'}` when the operator holds
 * none of the requested permissions. A status-only branch would fold that into the
 * expired card and tell the operator to *start over* — advice that cannot help them
 * once, because the request is fine and their authority is not.
 *
 * ⚠️ Lives HERE rather than in the page (where the old UI had it) so the pure logic is
 * unit-testable without rendering — the console's house split.
 */
export function classifyConsentFailure(err: unknown): TerminalKind | 'transport' {
  if (!(err instanceof ApiError)) return 'transport';
  if (oauthErrorCode(err.body) === 'invalid_scope') return 'insufficient-permissions';
  // 404 unknown id, 410 expired/already-handled — the load path's two outcomes, and
  // approve/deny losing the terminal-state race.
  if (err.status === 404 || err.status === 410) return 'invalid-or-expired';
  return 'transport';
}
